import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Fade } from 'react-awesome-reveal';
import { Button } from '../Button';
import { Icon } from '../Icon';
import { FeedbackList } from '../FeedbackList';
import { logEvent } from '../../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../../lib/shared/event-types';
import styles from './PlanLoaderProgressV3.css';

const stopPoints = [30, 60];
const radius = 45;
const circumference = 2 * Math.PI * radius;
const DELAY = 300;
const DELAY_MODAL_VISIBLE = 750;

const DEFAULT_QUESTIONS = {
    30: {
        title: 'Protect Your Data',
        description: 'Want to know if someone is using your identity right now?'
    },
    60: {
        title: 'Identity at Risk',
        description: 'Want to check in real-time if your data has been leaked?'
    }
};

function PlanLoaderProgressV3({
    finish = () => {},
    planTitle = 'Personal Digital Protection Plan',
    steps = ['Assessing user risks', 'Detecting vulnerabilities', 'Preparing personalized strategy'],
    usersCount = '24',
    hasRiskBadges = true,
    questions = DEFAULT_QUESTIONS
}) {
    const [progress, setProgress] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [modalClosing, setModalClosing] = useState(true);
    const finished = progress === 100;

    useEffect(() => {
        if (progress === 30) {
            logEvent(EVENT_TYPES.PLAN_LOADER_PAGE_QUESTION_VISITED, { questionNumber: 1 });
        }

        if (progress === 60) {
            logEvent(EVENT_TYPES.PLAN_LOADER_PAGE_QUESTION_VISITED, { questionNumber: 2 });
        }
    }, [progress]);

    useEffect(() => {
        if (finished) {
            finish();
        }

        if (!isPaused && stopPoints.includes(progress)) {
            setIsPaused(true);
            setTimeout(() => {
                setModalVisible(true);
            }, DELAY_MODAL_VISIBLE);
            setTimeout(() => {
                setModalClosing(false);
            }, DELAY_MODAL_VISIBLE + DELAY * 2);
        }

        if (!isPaused && progress < 100) {
            const timer = setTimeout(() => {
                setProgress(prev => prev + 1);
            }, 160);

            return () => clearTimeout(timer);
        }
    }, [progress, isPaused]);

    const closeModal = () => {
        setModalClosing(true);
        setTimeout(() => {
            setModalVisible(false);
            setModalClosing(false);
            setProgress((prev) => prev + 1);
            setIsPaused(false);
        }, DELAY);
    };

    const strokeDashoffset = circumference - (progress / 100) * circumference;

    const getQuestion = () => questions[progress] || {};

    const getPercentValue = ({ minValue, maxValue, value }) => {
        if (minValue >= value) {
            return 0;
        }

        if (value >= maxValue) {
            return 100;
        }

        return Math.round((value - minValue) / (maxValue - minValue) * 100);
    };

    const renderProgressBar = ({ description, maxValue, minValue, result }) => {
        const percentValue = getPercentValue({ minValue, maxValue, value: progress });

        return (
            <div className={styles.progressBar}>
                <div className={styles.progressBarContent}>
                    <span>{description}</span>
                    <span className={percentValue === 0 ? styles.zeroText : ''}>{percentValue}%</span>
                </div>
                <div className={styles.progressBarContainer}>
                    <div className={styles.progressBarActive} style={{ width: `${percentValue}%` }} />
                </div>
                {hasRiskBadges && progress >= maxValue
                    ? (
                        <Fade
                            triggerOnce
                            duration={300}
                        >
                            <div
                                className={styles.riskBlock}
                                style={{ color: result.color, backgroundColor: result.bgColor, borderColor: result.borderColor }}
                                role='status'
                            >
                                <Icon name={result.icon} />
                                <span>{result.title}</span>
                            </div>
                        </Fade>
                    )
                    : null
                }
            </div>
        );
    };

    return (
        <>
            <div className={styles.root}>
                <div className={styles.progressContainer} role='progressbar' aria-valuenow={progress}>
                    <svg width='98' height='98' aria-hidden='true'>
                        <circle
                            cx='49' cy='49' r={radius}
                            className={styles.circleBg}
                        />
                        <circle
                            cx='49' cy='49' r={radius}
                            className={styles.circle} style={{ strokeDashoffset }}
                        />
                    </svg>
                    <p className={styles.progressText}>{progress}%</p>
                </div>
                <h1 className={`${styles.title} main-title`}>
                    Creating Your <span>{planTitle}</span>
                </h1>
                <div className={styles.steps}>
                    {renderProgressBar({
                        description: steps[0],
                        maxValue: 30,
                        minValue: 0,
                        result: { icon: 'octagonWarning', title: 'High risk', color: 'var(--error-light-color)', borderColor: 'rgba(255, 0, 0, 0.1)', bgColor: 'rgba(255, 0, 0, 0.05)' }
                    })}
                    {renderProgressBar({
                        description: steps[1],
                        maxValue: 60,
                        minValue: 30,
                        result: { icon: 'alertTriangle', title: 'Moderate risk', color: 'var(--warning-color)', borderColor: 'rgba(255, 82, 0, 0.1)', bgColor: 'rgba(255, 82, 0, 0.05)' }
                    })}
                    {renderProgressBar({
                        description: steps[2],
                        maxValue: 100,
                        minValue: 60,
                        result: { icon: 'refresh', title: 'In progress', color: 'var(--success-dark-color)', borderColor: 'rgba(0, 127, 0, 0.1)', bgColor: 'rgba(0, 127, 0, 0.05)' }
                    })}
                </div>
                <h2 className={styles.secondaryTitle}>
                    <span>{usersCount}K+ users have chosen Futureproof</span><br />
                    to protect their data
                </h2>
                <p className={styles.description}>Original spelling and punctuation retained.</p>
                <FeedbackList />
            </div>
            {createPortal(
                <>
                    {modalVisible && (
                        <div className={`${styles.modal} ${modalClosing ? '' : styles.opening}`}>
                            <p className={styles.modalTitle}>{getQuestion().title}</p>
                            <p className={styles.modalDescription}>{getQuestion().description}</p>
                            <div className={styles.buttons}>
                                <Button
                                    label='Yes'
                                    size='medium'
                                    onClick={closeModal}
                                />
                                <Button
                                    label='No'
                                    type='secondary'
                                    size='medium'
                                    onClick={closeModal}
                                />
                            </div>
                        </div>
                    )}
                    <div className={`${styles.modalOverlay} ${modalVisible ? styles.opening : ''}`} />
                </>,
                document.body
            )}
        </>
    );
}

export {
    PlanLoaderProgressV3
};
